import { useAppSelector } from 'appState/store'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'
import { CSCreateQuery, CSRestQuery, Filter } from 'features/claims/claimTypes'
import { useEffect, useState } from 'react'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { httpTenantsService } from 'utils/http'
import { OaiUserCreate } from '../types/userTypes'
import CustomCheckbox from '../utils/CustomCheckbox'
import { extraPermissions, getPermissionList } from '../utils/UserConstants'

interface IProps {
  show: boolean
  hide: () => void
  title: string | JSX.Element
  userData: OaiUserCreate
  updateUser: (updatedUser: OaiUserCreate) => void
}

export const CSEditUserModal = ({ show, hide, title, userData, updateUser }: IProps) => {
  const [permissions, setPermissions] = useState<string[]>([])
  const [practices, setPractices] = useState<string[]>([])
  const [customerList, setCustomerList] = useState<any[]>([])
  const [isDirty, setIsDirty] = useState(false)

  const permissionsList = getPermissionList()
  const currentCustomer = useAppSelector((state) => state.auth.currentCustomer)
  const currentUser = useAppSelector((state) => state.auth.user)

  const isOutboundUser = currentUser?.email?.endsWith('@outbound.ai') && userData.email.endsWith('@outbound.ai')

  const outboundUserPermissions = [...permissionsList, ...extraPermissions()]
  const totalItems = customerList.length
  const mid = Math.ceil(totalItems / 2)

  const firstColumn = customerList.slice(0, mid)
  const secondColumn = customerList.slice(mid)
  useEffect(() => {
    getCustomerList()
  }, [])

  useEffect(() => {
    if (show) {
      setPermissions(userData.permissionIds || [])
      setPractices(userData.practiceIds || [])
      setIsDirty(false) // Reset dirty flag when modal opens
    }
  }, [show, userData])

  useEffect(() => {
    const originalPermissions = userData.permissionIds || []
    const originalPractices = userData.practiceIds || []

    const arePermissionsSame = arraysAreEqual(permissions, originalPermissions)
    const arePracticesSame = arraysAreEqual(practices, originalPractices)

    setIsDirty(!(arePermissionsSame && arePracticesSame))
  }, [permissions, practices, userData])

  function arraysAreEqual(arr1: string[], arr2: string[]) {
    if (arr1.length !== arr2.length) return false
    const sortedArr1 = [...arr1].sort()
    const sortedArr2 = [...arr2].sort()
    return sortedArr1.every((val, index) => val === sortedArr2[index])
  }

  function getCustomerList() {
    const query = new CSRestQuery(99998, 0)
    if (currentCustomer) {
      query.addFilter(new Filter('customerid', '=', currentCustomer))
    }

    const url = apiPaths.csSearch(RelativeRootEnum.cppuCustomers, new CSCreateQuery(query))

    httpTenantsService
      .get(url)
      .then((res) => {
        const customers = res.data.data
        if (customers && customers.length > 0) {
          setCustomerList(customers[0].practices.sort((a: any, b: any) => a.name.localeCompare(b.name)))
        } else {
          setCustomerList([])
        }
      })
      .catch((err) => {
        console.error('Error fetching practices:', err)
        setCustomerList([])
      })
  }

  const handleCheckboxChange = (value: string, setter: (values: string[]) => void, currentValues: string[]) => {
    const updatedValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value]
    setter(updatedValues)
  }

  const handleSubmit = () => {
    const updatedUser: OaiUserCreate = {
      ...userData,
      permissionIds: isOutboundUser
        ? permissions.filter((id) => outboundUserPermissions.some((perm) => perm.id === id))
        : permissions.filter((id) => permissionsList.some((perm) => perm.id === id)),
      practiceIds: practices.filter((id) => customerList.some((practice) => practice.practiceId === id)),
    }

    updateUser(updatedUser)
    hide()
  }

  const handleClose = () => {
    setPermissions([])
    setPractices([])
    hide()
  }

  return (
    <ObModal show={show} close={handleClose} title={title} customSize="medium" size="lg" height="auto">
      <Div>
        <PractiseGrid>
          <div>
            {userData.givenName} {userData.familyName}
          </div>
          <div>{userData.email}</div>
        </PractiseGrid>
        <SectionTitle>Permissions</SectionTitle>
        <PermissionsGrid>
          {isOutboundUser
            ? outboundUserPermissions.map((perm) => (
                <label key={perm.id}>
                  <CustomCheckbox
                    checked={permissions.includes(perm.id)}
                    onChange={() => handleCheckboxChange(perm.id, setPermissions, permissions)}
                  />
                  {perm.name}
                </label>
              ))
            : permissionsList.map((perm) => (
                <label key={perm.id}>
                  <CustomCheckbox
                    checked={permissions.includes(perm.id)}
                    onChange={() => handleCheckboxChange(perm.id, setPermissions, permissions)}
                  />
                  {perm.name}
                </label>
              ))}
        </PermissionsGrid>
        <SectionTitle>Access to Practices</SectionTitle>
        <PractiseGrid>
          <div>
            {firstColumn.map((practice) => (
              <label key={practice.practiceId}>
                <CustomCheckbox
                  checked={practices.includes(practice.practiceId)}
                  onChange={() => handleCheckboxChange(practice.practiceId, setPractices, practices)}
                />
                {practice.name}
              </label>
            ))}
          </div>

          <div>
            {secondColumn.map((practice) => (
              <label key={practice.practiceId}>
                <CustomCheckbox
                  checked={practices.includes(practice.practiceId)}
                  onChange={() => handleCheckboxChange(practice.practiceId, setPractices, practices)}
                />
                {practice.name}
              </label>
            ))}
          </div>
        </PractiseGrid>
      </Div>

      <FooterDiv>
        <Button radius="rounded" variant="primary-outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button radius="rounded" variant="primary" onClick={handleSubmit} disabled={!isDirty}>
          Save
        </Button>
      </FooterDiv>
    </ObModal>
  )
}

// Styling...

const Div = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const SectionTitle = styled.h4`
  margin: 1rem 0 0.5rem;
  font-weight: bold;
`

const PermissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const PractiseGrid = styled.div`
  display: flex;
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;

  /* each column */
  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const FooterDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #eee;
`
